import { useMemo, useRef, useCallback, useEffect, useState } from 'react'
import type { ResponseOption } from '../../../../../redux/flows/portal/lobby'
import {
  setPatientNotes,
  postPatientNotesAsync,
  priorityAnswersAsync,
  updateResponseOptionPriority,
  clearResponseOptionPriority,
} from '../../../../../redux/flows/portal/lobby'
import { useAppSelector, useAppDispatch } from '../../../../../redux/store'

const useInfo = () => {
  const dispatch = useAppDispatch()
  const currentPatient = useAppSelector((state) => state.lobbyPortal.patientDetails)
  const priorityAnswersStatus = useAppSelector((state) => state.lobbyPortal.priorityAnswersStatus)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  const [notesValue, setNotesValue] = useState('')

  const info = useMemo(() => {
    const responses = currentPatient?.questionResponses || []

    const sorted = [...responses].sort((a, b) => a.question.order - b.question.order)

    return sorted.map((item) => {
      const title = item.question.text.replace(/conditions?[^\w]?/i, '').trim()

      const options = [...item.responseOptions]

      const priorityOptions = options
        .filter((ro) => (ro.priority || 0) > 0)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .slice(0, 4)

      const nonPriorityOptions = options
        .filter((ro) => (ro.priority || 0) === 0)
        .sort((a, b) => a.option.order - b.option.order)

      const responseOptions = [...priorityOptions, ...nonPriorityOptions]

      return { title, responseOptions }
    })
  }, [currentPatient])

  const filteredInfo = info?.filter((group) => {
    const hasNoneOption = group.responseOptions.some((ro) => ro.option.text === 'None')
    return !hasNoneOption
  })

  const getAllResponseOptions = useCallback((): ResponseOption[] => {
    if (!currentPatient?.questionResponses) return []
    return currentPatient.questionResponses.flatMap((qr) => qr.responseOptions)
  }, [currentPatient])

  const hasMaxPriorities = useMemo(() => {
    const allOptions = getAllResponseOptions()
    const selectedCount = allOptions.filter((ro) => (ro.priority || 0) > 0).length
    return selectedCount >= 4
  }, [getAllResponseOptions])

  const handlePriorityToggle = useCallback(
    (responseOptionId: string) => {
      const allOptions = getAllResponseOptions()
      const selectedOption = allOptions.find((ro) => ro.id === responseOptionId)
      const currentPriority = selectedOption?.priority || 0

      const selectedOptions = allOptions
        .filter((ro) => (ro.priority || 0) > 0)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))

      if (currentPriority > 0) {
        dispatch(clearResponseOptionPriority({ responseOptionId }))

        selectedOptions
          .filter((ro) => (ro.priority || 0) > currentPriority)
          .forEach((ro) => {
            const newPriority = (ro.priority || 0) - 1
            dispatch(updateResponseOptionPriority({ responseOptionId: ro.id, priority: newPriority }))
          })

        dispatch(priorityAnswersAsync({ responseOptionId, priority: 0 }))
      } else {
        if (selectedOptions.length >= 4) {
          const toRemove = selectedOptions[selectedOptions.length - 1]
          if (toRemove) {
            dispatch(clearResponseOptionPriority({ responseOptionId: toRemove.id }))
            dispatch(priorityAnswersAsync({ responseOptionId: toRemove.id, priority: 0 }))
          }
        }

        const newPriority = selectedOptions.length + 1

        dispatch(updateResponseOptionPriority({ responseOptionId, priority: newPriority }))
        dispatch(priorityAnswersAsync({ responseOptionId, priority: newPriority }))
      }
    },
    [dispatch, getAllResponseOptions],
  )

  const handleNotesChange = useCallback(
    (value: string) => {
      dispatch(setPatientNotes(value))
      setNotesValue(value)
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }

      debounceTimer.current = setTimeout(() => {
        const patientId = currentPatient?.patient?.id
        if (patientId) {
          dispatch(postPatientNotesAsync({ patientId, notes: value }))
        }
      }, 1000)
    },
    [dispatch, currentPatient?.patient?.id],
  )

  useEffect(() => {
    if (currentPatient?.patient?.notes) {
      setNotesValue(currentPatient.patient.notes)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  return {
    info,
    filteredInfo,
    currentPatient,
    notesValue,
    handleNotesChange,
    handlePriorityToggle,
    priorityAnswersStatus,
    hasMaxPriorities,
  }
}

export default useInfo
